import * as Sentry from '@sentry/react';
// @ts-expect-error - @tgtg/core/type/object is not typed
import { isObject } from '@tgtg/core/type/object';

interface SentryMessage {
  message: string | Error;
  extra?: {
    apiUrl: string;
    sentryData?: string;
    step?: string;
    method?: string;
    error?: string;
    componentStack?: string;
  };
  tags: { [key: string]: string };
  forceException?: boolean;
}

interface CaptureError {
  response?: {
    status: string;
  };
  config?: {
    method: string;
    url: string;
  };
  [key: string]: unknown;
}

const blockCSRFError = ({
  message,
  tags,
  extra,
}: Omit<SentryMessage, 'forceException'>): boolean => {
  if (!extra || message !== 'MyCharity API Error') {
    return false;
  }
  const stringifiedError = JSON.stringify({
    errors: [{ code: 'FORBIDDEN', message: 'Invalid CSRF token' }],
  });
  const { apiUrl, sentryData } = extra;

  if (apiUrl !== '/web/auth/v3/login' || sentryData !== stringifiedError) {
    return false;
  }

  console.error('Sentry: This error will not be logged to Sentry', { message, extra, tags });
  return true;
};

const capture = ({ message, tags, extra, forceException = false }: SentryMessage) => {
  if (blockCSRFError({ message, extra, tags })) {
    return;
  }

  return Sentry.withScope((scope) => {
    if (tags && isObject(tags)) {
      scope.setTags(tags);
    }

    if (extra && isObject(extra)) {
      scope.setExtras(extra);
    }
    scope.setFingerprint([window.location.pathname]);

    if (forceException || message instanceof Error) {
      const info = { componentStack: extra?.componentStack };
      Sentry.captureReactException(message, info);
    } else {
      Sentry.captureMessage(message);
    }
  });
};

export const sentryCapture = (
  message: SentryMessage['message'],
  errorTag: string,
  error: Error | CaptureError | null = null,
  componentStack: string | undefined = undefined,
  step = 'unknown',
) => {
  const err = error as Record<string, unknown>;
  return capture({
    message,
    tags: {
      error: errorTag,
      step,
      'status code': (err.response as { status: string } | undefined)?.status ?? 'unknown',
    },
    extra: {
      componentStack,
      step,
      error: error?.toString() ?? undefined,
      method: (err.config as { method: string } | undefined)?.method ?? 'unknown',
      apiUrl: (err.config as { url: string } | undefined)?.url ?? 'unknown',
    },
  });
};
